import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ActivityIndicator, MinimalHeader } from '@bosch/react-frok'

import LineOnboarding from '@/ui/components/LineOnboarding/LineOnboarding'
import { useSpace } from '@/hooks/useSpace'
import { useConfig } from '@/hooks/useConfig'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useLineOnboarding } from '@/hooks/useLineOnboarding'
import useVirtualKeyboard from '@/hooks/useVirtualKeyboard'
import storage from '@/utils/storage'
import type { LineOption } from '@/utils/storage'
import type { RootState } from '@/utils/store/store'
import type { AssistantConfig } from '@/shared/types/space'
import type { MetadataLine } from '@/ui/advancedAssistants/MMA/types/MMA'
import AccessRoleFilter from '@/utils/accessRoleFilter'

import './OnboardingPage.scss'

interface OnboardingPageProps {
    /** The space the user is going to: the lines are asked for, and kept, per space */
    spaceId: string
    onComplete: () => void
}

const OnboardingPage = ({ spaceId, onComplete }: OnboardingPageProps) => {
    const { darkTheme } = useSelector((state: RootState) => state.application)
    useVirtualKeyboard()
    const { spaces, spacesLoading, loadAssistantMetadata } = useSpace()
    const { getConfig } = useConfig()
    const { getApiToken, information } = useAuthentication()
    const configEndpoint = getConfig()?.endpoint?.replace(/\/$/, '') ?? ''
    const userRoles: string[] = information?.roles ?? []
    const [loadingLines, setLoadingLines] = useState(true)

    const isAssistantAccessible = (assistant: AssistantConfig) => {                                                                                                                                                                                                                               
        if (!assistant.access?.length) return true                                                                                                                                                                                                                                                      
        if (!userRoles.length) return true                                                                                                                                                                                                                                                              
        return AccessRoleFilter.filterAssistants(userRoles, [assistant]).length > 0                                                                                                                                                                                                              
    }
    
    const resolutionSpace = spaces.find(s => s.id === spaceId)

    const resolutionAssistant = resolutionSpace?.assistants.find(a =>
        a.config.component === 'MMA' &&
        a.config.id.endsWith('-resolution') &&
        isAssistantAccessible(a.config)
    )

    // Wait for auth and spaces to be ready, then fetch line metadata from the resolution assistant
    useEffect(() => {
        if (spacesLoading && storage.getAvailableLines(spaceId).length === 0) return

        if (!resolutionAssistant) {
            setLoadingLines(false)
            return
        }

        if (resolutionAssistant.metadata) {
            setLoadingLines(false)
            return
        }

        loadAssistantMetadata(resolutionAssistant.config.id).finally(() => {
            setLoadingLines(false)
        })
        // the assistant is read by id: its object changes with the metadata that is loaded here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spacesLoading, resolutionAssistant?.config.id, loadAssistantMetadata])

    // Lines from resolution assistant metadata; fall back to storage cache if unavailable
    const lineElements: MetadataLine = resolutionAssistant?.metadata?.filter?.line?.elements ?? {}
    const metadataLines: LineOption[] = Object.entries(lineElements).map(([key, val]) => ({
        value: key,
        label: val?.displayName ?? key,
    }))
    const availableLines = metadataLines.length > 0 ? metadataLines : storage.getAvailableLines(spaceId)

    const { needsOnboarding, isCheckingPreferences, isSaving, saveError, completeOnboarding, clearSaveError } =
        useLineOnboarding({
            spaceId,
            endpoint: configEndpoint,
            getApiToken,
            preferencesApiEnabled: getConfig()?.features?.preferencesApi ?? false,
        })

    // The space has no resolution assistant the user can use: there are no lines to ask for
    useEffect(() => {
        if (!loadingLines && !resolutionAssistant) onComplete()
    }, [loadingLines, resolutionAssistant, onComplete])

    // Dismiss overlay once we know onboarding is not needed
    useEffect(() => {
        if (loadingLines || isCheckingPreferences) return

        if (storage.getOnboardingCompleted(spaceId) || (availableLines.length > 0 && !needsOnboarding)) {
            onComplete()
        }
    }, [loadingLines, isCheckingPreferences, needsOnboarding, availableLines.length, onComplete, spaceId])

    const handleComplete = async (preferredLines: LineOption[]) => {
        await completeOnboarding(preferredLines)
        onComplete()
    }

    const isLoading = loadingLines || isCheckingPreferences

    return (
        <div className={`onboarding-page -${darkTheme ? 'dark' : 'light'}-mode`}>
            <MinimalHeader />

            {isLoading && (
                <div className="onboarding-page__loader">
                    <ActivityIndicator size='small' />
                </div>
            )}

            <div className="onboarding-page__body">
                {!loadingLines && !isCheckingPreferences && needsOnboarding && (
                    <LineOnboarding
                        availableLines={availableLines}
                        spaceName={resolutionSpace?.name ?? ''}
                        onComplete={handleComplete}
                        isSaving={isSaving}
                        saveError={saveError}
                        onClearSaveError={clearSaveError}
                    />
                )}
            </div>
        </div>
    )
}

export default OnboardingPage
